// src/components/LocationMap/LocationMap.tsx
import React, { useEffect, useRef, useState } from 'react';
import styles from './LocationMap.module.css';
import { useYandexMaps } from '../../hooks/useYandexMaps.ts';
import useInView from '../Animation/useInView.ts';

interface Location {
  id: string;
  latitude: number;
  longitude: number;
  address: string;
}

interface LocationMapProps {
  locations: Location[];
  selectedLocationId?: string;
  onLocationSelect?: (locationId: string) => void;
  className?: string;
}

const YANDEX_API_KEY = 'c267d18a-9e38-49de-8f5e-6b3815716505';

const CUSTOM_MARKER_STYLES = `
  .custom-marker {
    display: flex;
    flex-direction: column;
    align-items: center;
    pointer-events: none;
    filter: drop-shadow(0 2px 4px rgba(124, 58, 237, 0.4));
  }
  .custom-marker .dot {
    width: 17px;
    height: 17px;
    border-radius: 50%;
    background: #494949;
    background: linear-gradient(135deg, rgba(109, 40, 217, 0.6) 0%, rgba(124, 58, 237, 0.9) 100%);
    transition: all 0.3s ease;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.4);
    animation: flicker 1.5s infinite alternate;
  }
  .custom-marker.highlighted .dot {
    width: 23px;
    height: 23px;
    background: #7c3aed;
    background: linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%);
    box-shadow: 
      0 0 0 3px rgba(124, 58, 237, 0.4),
      0 0 12px rgba(124, 58, 237, 0.7),
      0 0 20px rgba(167, 139, 250, 0.5);
    animation: pulse-fire 1.2s infinite;
  }
  @keyframes flicker {
    0%, 100% { transform: scale(1) rotate(0deg); opacity: 1; }
    25% { transform: scale(0.95) rotate(-1deg); opacity: 0.9; }
    75% { transform: scale(1.05) rotate(1deg); opacity: 1; }
  }
  @keyframes pulse-fire {
    0%, 100% { transform: scale(1); box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.4), 0 0 12px rgba(124, 58, 237, 0.7); }
    50% { transform: scale(1.1); box-shadow: 0 0 0 5px rgba(124, 58, 237, 0.5), 0 0 16px rgba(124, 58, 237, 0.9); }
  }
`;

const STYLES_ID = 'custom-marker-styles-inline';

const LocationMap: React.FC<LocationMapProps> = ({
  locations,
  selectedLocationId,
  onLocationSelect,
  className = '',
}) => {
  const [activeLocationId, setActiveLocationId] = useState(
    selectedLocationId || locations[0]?.id || ''
  );

  const prevActiveIdRef = useRef<string | null>(null);
  const activeLocation = locations.find(loc => loc.id === activeLocationId) || locations[0];

  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<any>(null);
  const markers = useRef<Record<string, any>>({});
  const layouts = useRef<{ default: any; highlighted: any } | null>(null);
  const isMapInitialized = useRef(false);

  const { ref, isInView } = useInView({ threshold: 0.1, triggerOnce: true });
  const mapsReady = useYandexMaps(YANDEX_API_KEY);

  const handleLocationClick = (locationId: string) => {
    setActiveLocationId(locationId);
    onLocationSelect?.(locationId);
  };

  useEffect(() => {
    if (document.getElementById(STYLES_ID)) return;

    const style = document.createElement('style');
    style.id = STYLES_ID;
    style.textContent = CUSTOM_MARKER_STYLES;
    document.head.appendChild(style);

    return () => {
      const el = document.getElementById(STYLES_ID);
      if (el) el.remove();
    };
  }, []);

  useEffect(() => {
    if (!mapsReady || !mapRef.current || isMapInitialized.current || locations.length === 0) return;

    window.ymaps.ready(() => {
      const ym = window.ymaps;

      const DefaultLayout = ym.templateLayoutFactory.createClass(
        `<div class="custom-marker"><div class="dot"></div></div>`
      );

      const HighlightedLayout = ym.templateLayoutFactory.createClass(
        `<div class="custom-marker highlighted"><div class="dot"></div></div>`
      );

      layouts.current = { default: DefaultLayout, highlighted: HighlightedLayout };

      mapInstance.current = new ym.Map(mapRef.current, {
        center: [activeLocation.latitude, activeLocation.longitude],
        zoom: 16,
        controls: [],
      });

      locations.forEach(loc => {
        const isSelected = loc.id === activeLocationId;
        const placemark = new ym.Placemark(
          [loc.latitude, loc.longitude],
          {
            id: loc.id,
            label: loc.address,
          },
          {
            iconLayout: isSelected ? HighlightedLayout : DefaultLayout,
            iconShape: {
              type: 'Circle',
              coordinates: [0, 0],
              radius: 10.5,
            },
          }
        );

        placemark.events.add('click', () => handleLocationClick(loc.id));
        mapInstance.current.geoObjects.add(placemark);
        markers.current[loc.id] = placemark;
      });

      isMapInitialized.current = true;
      prevActiveIdRef.current = activeLocationId;
    });
  }, [mapsReady, locations, activeLocation]);

  useEffect(() => {
    if (!mapInstance.current || !isMapInitialized.current || !layouts.current) return;

    const oldId = prevActiveIdRef.current;
    const newId = activeLocationId;

    if (oldId === newId) return;

    const { default: DefaultLayout, highlighted: HighlightedLayout } = layouts.current;

    if (oldId && markers.current[oldId]) {
      markers.current[oldId].options.set('iconLayout', DefaultLayout);
    }

    if (markers.current[newId]) {
      markers.current[newId].options.set('iconLayout', HighlightedLayout);
      mapInstance.current.setCenter([activeLocation.latitude, activeLocation.longitude], 16);
    }

    prevActiveIdRef.current = newId;
  }, [activeLocationId, activeLocation]);

  useEffect(() => {
    return () => {
      if (mapInstance.current) {
        Object.values(markers.current).forEach(marker => {
          mapInstance.current.geoObjects.remove(marker);
        });
        mapInstance.current.destroy();
        mapInstance.current = null;
      }
    };
  }, []);

  if (!locations?.length) return null;

  return (
    <div
      id="MapId"
      ref={ref}
      className={`${styles.locationCard} ${className} ${isInView ? styles.visible : ''}`}
    >
      <div className={styles.cardContent}>
        <div className={styles.addressesSection}>
          <h3 className={styles.sectionTitle}>Наши адреса</h3>
          <div className={styles.addressesList}>
            {locations.map(loc => (
              <div
                key={loc.id}
                className={`${styles.addressItem} ${activeLocationId === loc.id ? styles.addressItemActive : ''}`}
                onClick={() => handleLocationClick(loc.id)}
              >
                <div className={styles.addressMarker} />
                <div className={styles.addressInfo}>
                  <p className={styles.locationAddress}>{loc.address}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.mapSection}>
          <div ref={mapRef} className={styles.mapContainer} />
        </div>
      </div>
    </div>
  );
};

export default LocationMap;